import React, { useState, useRef, useCallback, useEffect } from "react";
import { Heart } from "lucide-react";
import { motion, useAnimationControls } from "framer-motion";
import { musicClient, fmtCount } from "@/lib/musicApi";

// Multi-tap cumulative like. Taps accumulate client-side for 800ms, then one
// POST {count} is sent. Heart stays red once the user has liked (no unlike).
export const LikeButton = ({ trackId, initialCount = 0, initialLiked = false, size = 20, showCount = true }) => {
  const [count, setCount] = useState(initialCount);
  const [liked, setLiked] = useState(initialLiked);
  const pending = useRef(0);
  const timer = useRef(null);
  const controls = useAnimationControls();

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const flush = useCallback(async () => {
    const n = pending.current;
    pending.current = 0;
    if (!n) return;
    try {
      const { data } = await musicClient.post(`/tracks/${trackId}/like`, { count: n });
      if (typeof data.like_count === "number") setCount(data.like_count);
    } catch {}
  }, [trackId]);

  const tap = useCallback((e) => {
    e.stopPropagation();
    setLiked(true);
    setCount((c) => c + 1);
    pending.current += 1;
    controls.start({ scale: [1, 1.35, 1], transition: { duration: 0.25 } });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 800);
  }, [flush, controls]);

  return (
    <button onClick={tap} data-testid={`like-btn-${trackId}`} className="flex items-center gap-1.5" aria-label="Like">
      <motion.span animate={controls} style={{ display: "inline-flex" }}>
        <Heart size={size} style={{ color: liked ? "#ef4444" : "var(--text-secondary)" }} fill={liked ? "#ef4444" : "none"} />
      </motion.span>
      {showCount && <span className="text-xs tabular-nums" data-testid={`like-count-${trackId}`} style={{ color: "var(--text-secondary)" }}>{fmtCount(count)}</span>}
    </button>
  );
};

export default LikeButton;
